import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Guidelines from './pages/Guidelines';
import GuidelineDetail from './pages/GuidelineDetail';
import Organisations from './pages/Organisations';
import OrganisationDetail from './pages/Organisations/OrganisationDetail/OrganisationDetail';
import CeleryMonitor from './pages/CeleryMonitor';
import SystemHealth from './pages/SystemHealth';

// ── Protected Route Wrapper ────────────────────────────────────────
const REDIRECT_KEY = 'admin_redirect_in_progress';
const REDIRECT_TIMEOUT = 5000; // 5 seconds

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isSuperuser, loading } = useAuth();
  const demoBase = process.env.REACT_APP_DEMO_FRONTEND_URL || '';

  React.useEffect(() => {
    if (loading) return;

    // Check if a redirect is already in progress (prevents loops)
    const redirectTimestamp = sessionStorage.getItem(REDIRECT_KEY);
    if (redirectTimestamp) {
      const elapsed = Date.now() - parseInt(redirectTimestamp, 10);
      if (elapsed < REDIRECT_TIMEOUT) {
        console.log('Redirect in progress, skipping to prevent loop');
        return;
      }
      // Redirect timed out, clear it
      sessionStorage.removeItem(REDIRECT_KEY);
    }

    if (!isAuthenticated) {
      // Not authenticated - redirect to login
      if (demoBase) {
        console.log('Not authenticated, redirecting to demo frontend login...');
        sessionStorage.setItem(REDIRECT_KEY, Date.now().toString());
        const returnUrl = encodeURIComponent(window.location.origin + window.location.pathname);
        window.location.href = `${demoBase}/login?returnUrl=${returnUrl}`;
      }
    } else if (!isSuperuser) {
      // Authenticated but not superuser - redirect to demo dashboard with error
      if (demoBase) {
        console.log('User is not a superuser, redirecting to demo dashboard...');
        sessionStorage.setItem(REDIRECT_KEY, Date.now().toString());
        window.location.href = `${demoBase}?error=superuser_required`;
      }
    } else {
      // Successfully authenticated as superuser - clear any redirect flags
      sessionStorage.removeItem(REDIRECT_KEY);
      sessionStorage.removeItem('demo_redirect_in_progress');
    }
  }, [loading, isAuthenticated, isSuperuser, demoBase]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show loading while not authenticated (redirect in progress)
  if (!isAuthenticated || !isSuperuser) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="guidelines" element={<Guidelines />} />
            <Route path="guidelines/:id" element={<GuidelineDetail />} />
            <Route path="organisations" element={<Organisations />} />
            <Route path="organisations/:id" element={<OrganisationDetail />} />
            <Route path="celery" element={<CeleryMonitor />} />
            <Route path="celery/:taskId" element={<CeleryMonitor />} />
            <Route path="system-health" element={<SystemHealth />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
};

export default App;
